import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../AppContext";
import { showCustomConfirmDialog } from "../../../common/components/dialogs/AppConfirmDialog";
import { useSupportUnreadCount } from "../../../common/infrastructure/hooks/useSupportUnreadCount";
import { useVisualViewport } from "../../../common/infrastructure/hooks/useVisualViewport";
import { routePaths } from "../../../common/infrastructure/routes/routePaths";
import {
  AI_DEFAULT_MAX_IMAGE_BYTES,
  AI_DEFAULT_MAX_IMAGES_PER_MESSAGE,
  AI_DEFAULT_MAX_MESSAGE_CHARS,
  IAiConversationListItem,
  aiAssistantTexts,
} from "../models/aiAssistantModels";
import { AiDeleteConversationMessage } from "../components/AiDeleteConversationMessage";
import { formatUsedPercent } from "../utilities/aiAssistantDataUtility";
import { useAiAssistant } from "./useAiAssistant";
import { useAiComposer } from "./useAiComposer";

/** Room left under the composer while the keyboard is up. */
const KEYBOARD_GAP_PX = 8;
const MIN_KEYBOARD_PANEL_HEIGHT_PX = 240;

/**
 * Everything the assistant page needs: the conversation state, the composer, the conversations
 * drawer on phones, the image preview, and the phone-keyboard fit (iOS keeps the layout height
 * when the keyboard opens, so the panel is capped to the visible area instead).
 */
export const useAiAssistantPage = () => {
  const navigate = useNavigate();
  const { isDesktopOrTablet } = useAppContext();
  const supportUnreadCount = useSupportUnreadCount();
  const assistant = useAiAssistant();
  const { status } = assistant;

  const composer = useAiComposer({
    maxChars: status?.maxMessageChars ?? AI_DEFAULT_MAX_MESSAGE_CHARS,
    maxImages: status?.maxImagesPerMessage ?? AI_DEFAULT_MAX_IMAGES_PER_MESSAGE,
    maxImageBytes: status?.maxImageBytes ?? AI_DEFAULT_MAX_IMAGE_BYTES,
    isSending: assistant.isSending,
    disabled: assistant.banner !== null,
    onSubmit: assistant.send,
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  // ---- Phone keyboard ----

  const { height: visibleHeight, isKeyboardOpen } = useVisualViewport();
  const panelTopRef = useRef<HTMLDivElement>(null);
  const [panelTop, setPanelTop] = useState(0);
  const isPhoneKeyboardOpen = !isDesktopOrTablet && isKeyboardOpen;

  useEffect(() => {
    if (!isPhoneKeyboardOpen) return;
    // iOS pans the page to reveal the focused box; the panel is fitted to the visible area
    // instead, so the page itself must stay where it is.
    window.scrollTo(0, 0);
    setPanelTop(panelTopRef.current?.getBoundingClientRect().top ?? 0);
  }, [isPhoneKeyboardOpen, visibleHeight]);

  const keyboardMaxHeight = isPhoneKeyboardOpen
    ? Math.max(MIN_KEYBOARD_PANEL_HEIGHT_PX, Math.floor(visibleHeight - panelTop - KEYBOARD_GAP_PX))
    : undefined;

  // ---- Actions ----

  const startNewConversation = useCallback(() => {
    assistant.startNewConversation();
    setIsDrawerOpen(false);
    // On a phone, focusing would throw the keyboard up before the user chose to type.
    if (isDesktopOrTablet) composer.focus();
  }, [assistant, composer, isDesktopOrTablet]);

  const selectConversation = useCallback(
    (item: IAiConversationListItem) => {
      assistant.selectConversation(item.id);
      setIsDrawerOpen(false);
    },
    [assistant],
  );

  const requestDeleteConversation = useCallback(
    (item: IAiConversationListItem) => {
      // A React element, not a string: the confirm dialog injects strings as HTML, and the title
      // was written by the model.
      showCustomConfirmDialog(
        createElement(AiDeleteConversationMessage, { title: item.title || aiAssistantTexts.untitledConversation }),
        () => assistant.deleteConversation(item.id),
        undefined,
        "warning",
        aiAssistantTexts.deleteConfirmYes,
        aiAssistantTexts.deleteConfirmNo,
      );
    },
    [assistant],
  );

  const openTeamRequests = useCallback(() => navigate(routePaths.supportTickets), [navigate]);
  const writeToTeam = useCallback(() => navigate(routePaths.supportNew), [navigate]);

  const usedPercent = status?.usedPercent ?? 0;

  return {
    ...assistant,
    composer,
    isDesktopOrTablet,
    hasUnreadTeamReplies: supportUnreadCount > 0,
    usedPercentLabel: formatUsedPercent(usedPercent),
    usedPercent: Math.min(100, usedPercent),
    isDrawerOpen,
    openDrawer: () => setIsDrawerOpen(true),
    closeDrawer: () => setIsDrawerOpen(false),
    previewImageUrl,
    openImagePreview: (url: string) => setPreviewImageUrl(url),
    closeImagePreview: () => setPreviewImageUrl(null),
    panelTopRef,
    keyboardMaxHeight,
    isPhoneKeyboardOpen,
    startNewConversation,
    selectConversation,
    requestDeleteConversation,
    openTeamRequests,
    writeToTeam,
  };
};
